import { Component, Input } from "@angular/core";
import { InfoDrawerComponent, InfoItem } from "../info-drawer/info-drawer.component";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { DocMetadataHandler } from "src/app/add-doc-metadata/services/add-doc-metadata.interface";
import { DocMetadata, DocMetadataDto, DocMetadataInfo, EntityType } from "src/app/add-doc-metadata/model/doc-metadata";
import { AppState } from "@shared/redux/store";
import { NgRedux } from "@shared/redux/redux-compatibility.service";
import { TranslateModule } from "@ngx-translate/core";
import { RevDoc } from "@shared/models/rev.model";
import { CatDoc } from "@shared/models/cat.model";


@Component({
  selector: 'doc-metadata-info-drawer',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TranslateModule,
    InfoDrawerComponent
  ],
  providers: [
    { provide: DocMetadataHandler, useClass: DocMetadataHandler }
  ],
  templateUrl: './doc-metadata-info-drawer.component.html',
  styleUrls: ['./doc-metadata-info-drawer.component.scss']
})
export class DocMetadataInfoDrawerComponent {


   @Input() displayTitle: string = 'Document Metadata';
   @Input({required: true}) doc!: RevDoc | CatDoc;
   @Input() isPrecheck: boolean = true;
   @Input() lang: string = 'en';
   @Input() entityType: EntityType = 'DOCUMENT';
   metadataItems: InfoItem[] = [];

   resolved: boolean = false;
  saving = false;
  private cachedMetadataDefinition: DocMetadataInfo | null = null;


  constructor(private docMetadataHandler: DocMetadataHandler,
    private ngRedux: NgRedux<AppState>) {

   }
   ngOnInit(): void {
      // Metadata is initialized when the drawer is opened.
      console.log(this.doc);
   }

   private loadDocumentMetadata(): void {
      this.docMetadataHandler.getDocMetadata(this.doc.docId, this.lang, this.isPrecheck).subscribe((metadata: DocMetadataDto[]) => {
        const metadataByField = new Map(metadata.map((item) => [item.field_name, item]));
        const metadataItems = this.docMetadataHandler.groups.map((group, index) => {
          const fieldMetadata = metadataByField.get(group.groupName);
          const options = this.docMetadataHandler.docMetadataByGroup[group.groupName] ?? [];
          const selectedValues = fieldMetadata?.lov?.map((value) => value.value_code) ?? [];
          const selectedValue = group.isMultiValued
            ? selectedValues
            : selectedValues[0] ?? null;

          if (fieldMetadata) {
            this.updateSelection(group.groupName, selectedValues, group.isMultiValued);
          } else {
            this.docMetadataHandler.selections[group.groupName] = group.isMultiValued ? [] : null;
          }

          return {
            key: group.groupName,
            label: group.groupLabel,
            value: selectedValue,
            isRequired: group.isRequired,
            isEditable: true,
            options: options.map((option) => ({
              label: option.fieldLabel,
              value: option.fieldValue
            })),
            isMultiSelect: group.isMultiValued,
            selectedValue,
            separatorBefore: index === 0,
            readonly: group.hasDefaultValue
          } as InfoItem;
        });

        this.metadataItems = this.metadataItems.concat(metadataItems);
        this.resolved = true;
      }, () => {
        this.resolved = true;
      });
   }

   private initializeDrawerMetadata(): void {
      this.metadataItems = this.docMetadataHandler.getBasicDocMetadata(this.doc, this.isPrecheck);
      this.resolved = false;

      if (this.cachedMetadataDefinition) {
        this.docMetadataHandler.groups = this.cachedMetadataDefinition.groups;
        this.docMetadataHandler.docMetadataByGroup = this.cachedMetadataDefinition.docMetadataByGroup;
        this.loadDocumentMetadata();
        return;
      }

      const catVersionId = this.doc?.catVersionId;
      const envId = this.ngRedux.getState().curEnv?.id;

      if (envId == null || catVersionId == null) {
        this.loadDocumentMetadata();
        return;
      }

      this.docMetadataHandler.getDocMetadataDefinition(
        envId,
        catVersionId,
        this.lang,
        null,
        this.entityType
      ).subscribe({
        next: (definition) => {
          this.cachedMetadataDefinition = definition;
          this.loadDocumentMetadata();
        },
        error: () => this.loadDocumentMetadata()
      });
   }

   private updateSelection(fieldName: string, values: string[], isMultiSelect: boolean): void {
      const options = this.docMetadataHandler.docMetadataByGroup[fieldName] ?? [];
      const selected = values
        .map((value) => options.find((option) => option.fieldValue === value))
        .filter((option): option is DocMetadata => option != null);

      this.docMetadataHandler.selections[fieldName] = isMultiSelect
        ? selected
        : selected[0] ?? (values[0] ? new DocMetadata(fieldName, fieldName, values[0], -1) : null);
   }

   onItemValueChange(item: InfoItem, value: unknown): void {
      if (!item.key) {
        return;
      }

      const options = this.docMetadataHandler.docMetadataByGroup[item.key] ?? [];

      if (item.isMultiSelect) {
        const selected = ((value as string[] | null) ?? [])
          .map((selectedValue) => options.find((option) => option.fieldValue === selectedValue))
          .filter((option): option is DocMetadata => option != null);

        this.docMetadataHandler.selections[item.key] = selected;
      } else {
        const selectedValue = value == null ? '' : String(value);
        const selected = options.find((option) => option.fieldValue === selectedValue)
          ?? new DocMetadata(item.key, item.label, selectedValue, -1);

        this.docMetadataHandler.selections[item.key] = selectedValue === '' ? null : selected;
      }

      item.value = value as string | number | boolean | null | undefined | string[];
      item.selectedValue = value as string | string[] | number | number[] | boolean | boolean[] | null;
   }

    discardChanges(): void {
      for (const item of this.metadataItems) {
        if (item.isEditable) {
          this.onItemValueChange(item, item.selectedValue ?? item.value);
        }
      }
    }

   showDrawer() {
    this.initializeDrawerMetadata();
   }

  saveChanges(onSuccess: () => void): void {
      const metadata = this.docMetadataHandler.getDocMetadataToAdd();
      if (!this.doc || !metadata || this.saving) {
        return;
      }

      this.saving = true;
      this.docMetadataHandler.saveDocMetadata(
        this.doc.docId,
        metadata,
        this.isPrecheck
      ).subscribe({
        next: () => {
          this.saving = false;
          onSuccess();
        },
        error: () => {
          this.saving = false;
        }
      });
   }

   isEditable() {

      return this.doc.statusCode !== 'COMPLETED' && this.doc.statusCode !== 'FINAL' && this.doc.statusCode !== 'ADDED_TO_CATALOG';
   }

}